import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  BookOpen,
  Calculator,
  FileText,
  ClipboardCheck,
  TrendingUp,
  MessageSquare,
  Phone,
  Mail,
  MapPin,
  Clock,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  Send,
  Quote,
  Building2,
  RefreshCw,
  Users,
  Briefcase
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Import pages
import KattavatPalvelut from "@/pages/KattavatPalvelut";
import Toimialaratkaisut from "@/pages/Toimialaratkaisut";
import TilitoimistonVaihtaminen from "@/pages/TilitoimistonVaihtaminen";
import ToimialaSivu from "@/pages/ToimialaSivu";
import AdminPage from "@/pages/AdminPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_kuula-ratkaisut/artifacts/shiyz0z6_kuulaslgg.jpeg";

// Images
const HERO_IMAGE = "https://images.unsplash.com/photo-1632835222323-c134fbb4e606?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxub3JkaWMlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzc0NzA3Mjg1fDA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMAGE = "https://images.pexels.com/photos/16193390/pexels-photo-16193390.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TESTIMONIAL_AVATAR = "https://images.unsplash.com/photo-1770871820934-daf713c304af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBhY2NvdW50YW50JTIwc21pbGluZ3xlbnwwfHx8fDE3NzQ3MDczMTN8MA&ixlib=rb-4.1.0&q=85";

// Services data
const services = [
{
  id: 1,
  title: "Kirjanpito",
  description: "Ammattitaitoinen kirjanpito yrityksellesi. Huolehdimme kuukausittaisesta kirjanpidosta, ALV-ilmoituksista ja raportoinnista.",
  icon: BookOpen,
  featured: true
},
{
  id: 2,
  title: "Palkanlaskenta",
  description: "Luotettava palkanlaskenta palvelu. Hoidamme palkanmaksun, työnantajamaksut ja vuosi-ilmoitukset.",
  icon: Calculator,
  featured: false
},
{
  id: 3,
  title: "HR-palvelut",
  description: "Kattavat henkilöstöhallinnon palvelut. Työsopimusten laadinta, työaikakirjanpito ja HR-neuvonta.",
  icon: Users,
  featured: true
},
{
  id: 4,
  title: "Tilinpäätökset",
  description: "Laadukkaat tilinpäätökset ja toimintakertomukset. Autamme yritystäsi täyttämään lakisääteiset velvoitteet.",
  icon: FileText,
  featured: false
},
{
  id: 5,
  title: "Veroilmoitukset",
  description: "Veroilmoitusten laadinta ja toimitus. Varmistamme, että verotuksesi on oikein ja ajallaan.",
  icon: ClipboardCheck,
  featured: false
},
{
  id: 6,
  title: "Verosuunnittelu",
  description: "Strateginen verosuunnittelu yrityksellesi. Optimoimme verotuksesi laillisesti ja tehokkaasti.",
  icon: TrendingUp,
  featured: true
},
{
  id: 7,
  title: "Konsultointi",
  description: "Asiantunteva konsultointi veroasioissa. Autamme sinua ymmärtämään verotusta ja tekemään parempia päätöksiä.",
  icon: MessageSquare,
  featured: false
}];


// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/#' + sectionId);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  // Dropdown menu items with routes
  const ratkaisutItems = [
    { label: "Kattavat tilitoimistopalvelut", path: "/ratkaisut/kattavat-palvelut", icon: Building2 },
    { label: "Toimialaratkaisut", path: "/ratkaisut/toimialaratkaisut", icon: Briefcase },
    { label: "Tilitoimiston vaihtaminen", path: "/ratkaisut/tilitoimiston-vaihtaminen", icon: RefreshCw },
  ];

  const palvelutItems = [
    { label: "HR-palvelut", section: "palvelut", icon: Users },
    { label: "Kirjanpito", section: "palvelut", icon: BookOpen },
    { label: "Palkanlaskenta", section: "palvelut", icon: Calculator },
    { label: "Tilinpäätökset", section: "palvelut", icon: FileText },
    { label: "Veroilmoitukset", section: "palvelut", icon: ClipboardCheck },
    { label: "Verosuunnittelu", section: "palvelut", icon: TrendingUp },
    { label: "Konsultointi", section: "palvelut", icon: MessageSquare },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#1a1a1a] shadow-lg' 
          : 'bg-[#1a1a1a]'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="logo-link"
          >
            <div className="bg-white rounded-lg p-1.5 shadow-md">
              <img
                src={LOGO_URL}
                alt="Kuulas Tilitoimisto"
                className="h-9 w-auto"
              />
            </div>
            <span className="hidden sm:block text-white font-semibold text-lg tracking-tight">
              Kuulas<span className="text-green-400">Tilitoimisto</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            {/* Ratkaisut Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-1 text-white/90 hover:text-white font-medium px-4 py-2 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="nav-ratkaisut"
                >
                  Ratkaisut
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 bg-white rounded-xl shadow-xl border-0 p-2" align="start">
                {ratkaisutItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={index}
                      asChild
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg hover:bg-slate-50 focus:bg-slate-50"
                    >
                      <Link to={item.path}>
                        <Icon className="h-5 w-5 text-[#22C55E]" />
                        <span className="text-slate-700 font-medium">{item.label}</span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Palvelut Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-1 text-white/90 hover:text-white font-medium px-4 py-2 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="nav-palvelut"
                >
                  Palvelut
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 bg-white rounded-xl shadow-xl border-0 p-2" align="start">
                {palvelutItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={index}
                      onClick={() => scrollToSection(item.section)}
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer rounded-lg hover:bg-slate-50 focus:bg-slate-50"
                    >
                      <Icon className="h-5 w-5 text-[#22C55E]" />
                      <span className="text-slate-700 font-medium">{item.label}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Tieto meistä */}
            <button
              onClick={() => scrollToSection('tieto-meista')}
              className="text-white/90 hover:text-white font-medium px-4 py-2 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="nav-tieto-meista"
            >
              Tieto meistä
            </button>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button
              onClick={() => scrollToSection('yhteystiedot')}
              className="bg-white text-[#1a1a1a] hover:bg-slate-100 px-6 py-2 rounded-full font-medium"
              data-testid="cta-ota-yhteytta"
            >
              Ota yhteyttä
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#1a1a1a] border-t border-white/10" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {/* Ratkaisut */}
            <div className="py-2">
              <p className="text-white/60 text-sm uppercase tracking-wider mb-2">Ratkaisut</p>
              {ratkaisutItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full text-left text-white/90 hover:text-white font-medium py-2 pl-4"
                >
                  {item.label}
                </Link>
              ))}
            </div>
            
            {/* Palvelut */}
            <div className="py-2 border-t border-white/10">
              <p className="text-white/60 text-sm uppercase tracking-wider mb-2 mt-2">Palvelut</p>
              {palvelutItems.map((item, index) => (
                <button
                  key={index}
                  onClick={() => scrollToSection(item.section)}
                  className="block w-full text-left text-white/90 hover:text-white font-medium py-2 pl-4"
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="border-t border-white/10 pt-4">
              <button
                onClick={() => scrollToSection('tieto-meista')}
                className="block w-full text-left text-white/90 hover:text-white font-medium py-2"
              >
                Tieto meistä
              </button>
              <Button
                onClick={() => scrollToSection('yhteystiedot')}
                className="w-full mt-4 bg-white text-[#1a1a1a] hover:bg-slate-100 rounded-full"
              >
                Ota yhteyttä
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="min-h-screen pt-20 hero-gradient" data-testid="hero-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* Left: Content */}
                    <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}>

                        <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#22C55E] mb-4 block">Kuulastilitoimisto

            </span>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 mb-6">
                            Luotettava kumppani 
                            <span className="text-[#22C55E]"> taloushallintoon</span>
                        </h1>
                        <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                            Kuulas Tilitoimisto tarjoaa kattavat taloushallinnon palvelut yrityksille. 
                            Autamme sinua keskittymään ydinliiketoimintaasi hoitamalla kirjanpidon, 
                            palkanlaskennan ja veroasiat puolestasi.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Button
                onClick={() => scrollToSection('yhteystiedot')}
                className="btn-primary bg-[#1a1a1a] hover:bg-[#000000] text-white px-8 py-3 rounded-full text-base"
                data-testid="hero-cta-primary">

                                Pyydä tarjous
                                <ChevronRight className="ml-2 h-4 w-4" />
                            </Button>
                            <Button
                onClick={() => scrollToSection('palvelut')}
                variant="outline"
                className="btn-secondary border-2 border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white px-8 py-3 rounded-full text-base"
                data-testid="hero-cta-secondary">

                                Tutustu palveluihin
                            </Button>
                        </div>
                    </motion.div>

                    {/* Right: Image with floating card */}
                    <motion.div
            className="relative"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}>

                        <div className="relative">
                            <img
                src={HERO_IMAGE}
                alt="Tilitoimiston ammattilaisia"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover" />

                            {/* Floating stat card */}
                            <div className="absolute -bottom-6 -left-6 glass-card rounded-xl p-4 shadow-lg">
                                <div className="flex items-center space-x-3">
                                    <div className="bg-[#22C55E] rounded-full p-2">
                                        <CheckCircle className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">100% Digitaalinen</p>
                                        <p className="font-semibold text-slate-900">Moderni taloushallinto</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>);

};

// Ratkaisut Section
const RatkaisutSection = () => {
  return (
    <section id="ratkaisut" className="py-20 bg-white" data-testid="ratkaisut-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}>

                    <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#22C55E] mb-4 block">
                        Ratkaisut
                    </span>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-4">
                        Kattavat tilitoimistopalvelut
                    </h2>
                    <p className="text-base text-slate-600 max-w-2xl mx-auto">
                        Tarjoamme räätälöityjä ratkaisuja kaiken kokoisille yrityksille. 
                        Palvelumme mukautuvat yrityksesi tarpeiden mukaan.
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-8">
                    {[
          {
            title: "Aloitteleville yrittäjille",
            description: "Helpota yrityksen alkutaivalta. Autamme perustamisasiakirjoissa ja kirjanpidon aloituksessa.",
            features: ["Yrityksen perustaminen", "Kirjanpito", "Konsultointi: vero-ohjaus"]
          },
          {
            title: "Kasvaville yrityksille",
            description: "Skaalautuvat palvelut kasvun tueksi. Keskity liiketoimintaan - me hoidamme talouden.",
            features: ["Kirjanpito", "Palkanlaskenta", "Tilinpäätökset"]
          },
          {
            title: "Vakiintuneille yrityksille",
            description: "Strateginen kumppanuus verosuunnittelussa ja talouden optimoinnissa.",
            features: ["Verosuunnittelu", "Konsultointi", "Tilinpäätökset"]
          }].
          map((solution, index) =>
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}>

                            <Card className="service-card h-full border border-slate-100 hover:border-[#22C55E]/30">
                                <CardContent className="p-6">
                                    <h3 className="text-xl font-semibold text-slate-800 mb-3">{solution.title}</h3>
                                    <p className="text-slate-600 mb-4">{solution.description}</p>
                                    <ul className="space-y-2">
                                        {solution.features.map((feature, i) =>
                  <li key={i} className="flex items-center text-sm text-slate-600">
                                                <CheckCircle className="h-4 w-4 text-[#22C55E] mr-2" />
                                                {feature}
                                            </li>
                  )}
                                    </ul>
                                </CardContent>
                            </Card>
                        </motion.div>
          )}
                </div>
            </div>
        </section>);

};

// Palvelut Section
const PalvelutSection = () => {
  return (
    <section id="palvelut" className="py-20 bg-[#F0FDF4]" data-testid="palvelut-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}>

                    <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#22C55E] mb-4 block">
                        Palvelut
                    </span>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-4">
                        Taloushallinnon palvelut
                    </h2>
                    <p className="text-base text-slate-600 max-w-2xl mx-auto">
                        Ammattitaitoinen tiimimme hoitaa yrityksesi taloushallinnon kokonaisvaltaisesti.
                    </p>
                </motion.div>

                {/* Bento Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={service.featured ? 'md:col-span-2 lg:col-span-1' : ''}>

                                <Card
                  className={`service-card h-full bg-white border border-slate-100 hover:border-[#22C55E]/30 ${
                  service.featured ? 'ring-2 ring-[#22C55E]/20' : ''}`
                  }
                  data-testid={`service-card-${service.id}`}>

                                    <CardContent className="p-6">
                                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${
                    service.featured ? 'bg-[#22C55E] text-white' : 'bg-[#F0FDF4] text-[#22C55E]'}`
                    }>
                                            <Icon className="h-6 w-6" />
                                        </div>
                                        <h3 className="text-xl font-semibold text-slate-800 mb-2">{service.title}</h3>
                                        <p className="text-slate-600">{service.description}</p>
                                    </CardContent>
                                </Card>
                            </motion.div>);

          })}
                </div>
            </div>
        </section>);

};

// Tieto meistä Section
const TietoMeistaSection = () => {
  return (
    <section id="tieto-meista" className="py-20 bg-white" data-testid="tieto-meista-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* Image */}
                    <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

                        <img
              src={ABOUT_IMAGE}
              alt="Moderni toimisto"
              className="rounded-2xl shadow-lg w-full h-auto object-cover" />

                    </motion.div>

                    {/* Content */}
                    <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

                        <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#22C55E] mb-4 block">
                            Tieto meistä
                        </span>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6">
                            Luotettava kumppanisi Tikkurilassa
                        </h2>
                        <p className="text-base text-slate-600 leading-relaxed mb-6">
                            Kuulas Tilitoimisto on moderni tilitoimisto, joka palvelee yrityksiä Tikkurilassa ja 
                            ympäri Suomea. Uskomme, että hyvä taloushallinto on jokaisen menestyvän yrityksen perusta.
                        </p>
                        <p className="text-base text-slate-600 leading-relaxed mb-6">
                            Tarjoamme henkilökohtaista palvelua ja räätälöityjä ratkaisuja, jotka sopivat juuri 
                            sinun yrityksesi tarpeisiin. Käytämme nykyaikaisia digitaalisia työkaluja, jotka 
                            tekevät taloushallinnosta sujuvaa ja tehokasta.
                        </p>
                        <div className="grid grid-cols-2 gap-6 mt-8">
                            <div>
                                <p className="text-3xl font-bold text-[#1a1a1a]">100%</p>
                                <p className="text-sm text-slate-600">Digitaalinen toiminta</p>
                            </div>
                            <div>
                                <p className="text-3xl font-bold text-[#1a1a1a]">✓</p>
                                <p className="text-sm text-slate-600">Luotettava kumppani</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>);

};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
  {
    name: "MrSweets Oy",
    company: "MrSweets Oy",
    location: "Vantaa",
    text: "Kuulas Tilitoimisto on ollut meille erinomainen kumppani. Ammattitaitoinen tiimi hoitaa kirjanpitomme luotettavasti ja saamme aina nopeasti apua kaikkiin talousasioihin liittyviin kysymyksiin. Suosittelemme lämpimästi!",
    avatar: TESTIMONIAL_AVATAR
  },
  {
    name: "MND Palvelu Oy",
    company: "MND Palvelu Oy",
    location: "Vantaa",
    text: "Olemme erittäin tyytyväisiä Kuulas Tilitoimiston palveluihin. He ymmärtävät yrityksemme tarpeet ja auttavat meitä tekemään parempia taloudellisia päätöksiä. Henkilökohtainen palvelu ja asiantuntemus on omaa luokkaansa.",
    avatar: TESTIMONIAL_AVATAR
  }];


  return (
    <section className="py-20 bg-[#F0FDF4]" data-testid="testimonials-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}>

                    <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#22C55E] mb-4 block">
                        Asiakaskokemuksia
                    </span>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-4">
                        Mitä asiakkaamme sanovat
                    </h2>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-8">
                    {testimonials.map((testimonial, index) =>
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}>

                            <Card className="testimonial-card border-0">
                                <CardContent className="p-8">
                                    <Quote className="h-8 w-8 text-[#22C55E]/30 mb-4" />
                                    <p className="text-slate-600 mb-6 italic">"{testimonial.text}"</p>
                                    <div className="flex items-center gap-4">
                                        <div className="bg-[#22C55E] text-white w-12 h-12 rounded-full flex items-center justify-center font-semibold text-lg">
                                            {testimonial.company.charAt(0)}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-800">{testimonial.company}</p>
                                            <p className="text-sm text-slate-500">{testimonial.location}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
          )}
                </div>
            </div>
        </section>);

};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.status === 'success') {
        toast.success('Kiitos yhteydenotostasi!', {
          description: 'Otamme sinuun yhteyttä pian.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      }
    } catch (error) {
      toast.error('Virhe!', {
        description: 'Viestin lähetys epäonnistui. Yritä uudelleen.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="yhteystiedot" className="py-20 bg-white" data-testid="contact-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}>

                    <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#22C55E] mb-4 block">
                        Yhteystiedot
                    </span>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-4">
                        Ota yhteyttä
                    </h2>
                    <p className="text-base text-slate-600 max-w-2xl mx-auto">
                        Jätä yhteystietosi, niin otamme sinuun yhteyttä mahdollisimman pian.
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-12">
                    {/* Info Card */}
                    <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

                        <div className="bg-[#F0FDF4] rounded-2xl p-8 h-full">
                            <h3 className="text-2xl font-semibold text-slate-800 mb-6">Miten toimimme?</h3>
                            
                            <div className="space-y-6">
                                <div className="flex items-start gap-4">
                                    <div className="bg-[#22C55E] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold flex-shrink-0">
                                        1
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-slate-800 mb-1">Jätä yhteystietosi</h4>
                                        <p className="text-slate-600 text-sm">
                                            Täytä lomake ja kerro tarpeistasi. Kaikki tiedot käsitellään luottamuksellisesti.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="bg-[#22C55E] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold flex-shrink-0">
                                        2
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-slate-800 mb-1">Otamme yhteyttä</h4>
                                        <p className="text-slate-600 text-sm">
                                            Soitamme sinulle mahdollisimman pian ja kartoitamme tarpeesi yhdessä.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="bg-[#22C55E] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold flex-shrink-0">
                                        3
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-slate-800 mb-1">Räätälöity tarjous</h4>
                                        <p className="text-slate-600 text-sm">
                                            Saat henkilökohtaisen tarjouksen, joka vastaa juuri sinun yrityksesi tarpeita.
                                        </p>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-green-200">
                                    <p className="text-slate-700 font-medium mb-1">Nopea vastaus</p>
                                    <p className="text-slate-600 text-sm">
                                        Vastaamme kaikkiin yhteydenottoihin arkisin.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Contact Form */}
                    <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

                        <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                                        Nimi *
                                    </label>
                                    <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Etunimi Sukunimi"
                    className="form-input"
                    data-testid="input-name" />

                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                                        Sähköposti *
                                    </label>
                                    <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="sinun@email.fi"
                    className="form-input"
                    data-testid="input-email" />

                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                                        Puhelin
                                    </label>
                                    <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+358 40 123 4567"
                    className="form-input"
                    data-testid="input-phone" />

                                </div>
                                <div>
                                    <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                                        Yritys
                                    </label>
                                    <Input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Yrityksen nimi"
                    className="form-input"
                    data-testid="input-company" />

                                </div>
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                                    Viesti *
                                </label>
                                <Textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Kerro meille tarpeistasi..."
                  className="form-input resize-none"
                  data-testid="input-message" />

                            </div>

                            <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary bg-[#1a1a1a] hover:bg-[#000000] text-white py-3 rounded-full"
                data-testid="contact-submit-button">

                                {isSubmitting ?
                'Lähetetään...' :

                <>
                                        Lähetä viesti
                                        <Send className="ml-2 h-4 w-4" />
                                    </>
                }
                            </Button>
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>);

};

// Footer Component
const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="footer-bg text-white py-16" data-testid="footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid md:grid-cols-4 gap-8 mb-12">
                    {/* Logo & Description */}
                    <div className="md:col-span-2">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-white rounded-lg p-1.5">
                                <img
                                    src={LOGO_URL}
                                    alt="Kuulas Tilitoimisto"
                                    className="h-9 w-auto"
                                />
                            </div>
                            <span className="text-white font-semibold text-lg tracking-tight">
                                Kuulas<span className="text-green-400">Tilitoimisto</span>
                            </span>
                        </div>

                        <p className="text-slate-300 max-w-md">
                            Kuulas Tilitoimisto - Luotettava kumppanisi taloushallintoon. 
                            Tarjoamme kattavat kirjanpito-, palkanlaskenta- ja veropalvelut yrityksille.
                        </p>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="font-semibold mb-4">Sivusto</h4>
                        <ul className="space-y-2">
                            <li>
                                <button
                  onClick={() => scrollToSection('ratkaisut')}
                  className="text-slate-300 hover:text-white transition-colors">

                                    Ratkaisut
                                </button>
                            </li>
                            <li>
                                <button
                  onClick={() => scrollToSection('palvelut')}
                  className="text-slate-300 hover:text-white transition-colors">

                                    Palvelut
                                </button>
                            </li>
                            <li>
                                <button
                  onClick={() => scrollToSection('tieto-meista')}
                  className="text-slate-300 hover:text-white transition-colors">

                                    Tieto meistä
                                </button>
                            </li>
                            <li>
                                <button
                  onClick={() => scrollToSection('yhteystiedot')}
                  className="text-slate-300 hover:text-white transition-colors">

                                    Ota yhteyttä
                                </button>
                            </li>
                        </ul>
                    </div>

                    {/* Info */}
                    <div>
                        <h4 className="font-semibold mb-4">Palvelut</h4>
                        <ul className="space-y-2 text-slate-300">
                            <li>Kirjanpito</li>
                            <li>Palkanlaskenta</li>
                            <li>HR-palvelut</li>
                            <li>Verosuunnittelu</li>
                            <li>Tilinpäätökset</li>
                        </ul>
                    </div>
                </div>

                {/* Copyright */}
                <div className="border-t border-slate-700 pt-8 text-center">
                    <p className="text-slate-400 text-sm">
                        © {new Date().getFullYear()} Kuulas Tilitoimisto. Kaikki oikeudet pidätetään.
                    </p>
                </div>
            </div>
        </footer>);

};

// Home Page
const Home = () => {
  return (
    <div className="min-h-screen">
            <Header />
            <main>
                <HeroSection />
                <RatkaisutSection />
                <PalvelutSection />
                <TietoMeistaSection />
                <TestimonialsSection />
                <ContactSection />
            </main>
            <Footer />
            <Toaster position="top-center" richColors />
        </div>);

};

// Page wrapper for subpages (includes Header and Footer)
const PageWrapper = ({ children }) => {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {children}
      </main>
      <Footer />
      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <div className="App">
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/ratkaisut/kattavat-palvelut" element={<PageWrapper><KattavatPalvelut /></PageWrapper>} />
                    <Route path="/ratkaisut/toimialaratkaisut" element={<PageWrapper><Toimialaratkaisut /></PageWrapper>} />
                    <Route path="/ratkaisut/tilitoimiston-vaihtaminen" element={<PageWrapper><TilitoimistonVaihtaminen /></PageWrapper>} />
                    <Route path="/toimialat/:toimiala" element={<PageWrapper><ToimialaSivu /></PageWrapper>} />
                    <Route path="/admin" element={<AdminPage />} />
                </Routes>
            </BrowserRouter>
        </div>);

}

export default App;